/**
 * Public lead-magnet form. Drop-in component used by the /insights
 * commercial article and (optionally) the /services Brand Studio feature.
 *
 * On submit:
 *   POST /api/brand/leads {name, email, company_name?, sector?, source}
 *   → creates a Brand project in DRAFT status with brief partially filled.
 *
 * On success: replaces the form with a thank-you state + CTA to /brand-studio.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Loader2, ArrowRight, CheckCircle2, Sparkles } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COPY = {
  en: {
    eyebrow: 'Start your brand in 60 seconds',
    title: "Tell us about your project — we'll take it from here.",
    note: 'We create a draft project in your name. No payment, no commitment — only when you decide to move forward.',
    name_ph: 'Your name *',
    email_ph: 'Work email *',
    company_ph: 'Company / brand name',
    sector_ph: 'Sector (optional)',
    submit: 'Start my brand project',
    confidentiality: 'Strictly confidential. Your data is never shared outside Grupo BMP.',
    error_required: 'Please enter your name and a valid email.',
    success_label: 'Lead received',
    success_title: 'Thank you, {name}.',
    success_body: 'We have created project {code} in draft. Our team will reach out within one business day to walk you through the next steps.',
    success_cta: 'Meanwhile, explore the plans',
    sectors: [
      'Wealth & financial services', 'Real estate & development', 'Hospitality & F&B',
      'Health & wellness', 'Retail & e-commerce', 'Professional services',
      'Technology & SaaS', 'Education & training', 'Industrial & trading', 'Other',
    ],
  },
  pt: {
    eyebrow: 'Comece a sua marca em 60 segundos',
    title: 'Conte-nos o seu projecto — nós tratamos do resto.',
    note: 'Criamos um projecto em rascunho em seu nome. Sem pagamento, sem compromisso — só quando decidir avançar.',
    name_ph: 'O seu nome *',
    email_ph: 'Email profissional *',
    company_ph: 'Empresa / nome da marca',
    sector_ph: 'Sector (opcional)',
    submit: 'Iniciar o meu projecto',
    confidentiality: 'Estritamente confidencial. Os seus dados nunca são partilhados fora do Grupo BMP.',
    error_required: 'Indique o seu nome e um email válido.',
    success_label: 'Lead recebido',
    success_title: 'Obrigado, {name}.',
    success_body: 'Criámos o projecto {code} em rascunho. A nossa equipa contactá-lo-á em um dia útil para apresentar os próximos passos.',
    success_cta: 'Entretanto, explore os planos',
    sectors: [
      'Gestão de património e finanças', 'Imobiliário e desenvolvimento', 'Hotelaria e F&B',
      'Saúde e bem-estar', 'Retalho e e-commerce', 'Serviços profissionais',
      'Tecnologia e SaaS', 'Educação e formação', 'Indústria e trading', 'Outro',
    ],
  },
  fr: {
    eyebrow: 'Lancez votre marque en 60 secondes',
    title: 'Parlez-nous de votre projet — nous prenons le relais.',
    note: "Nous créons un projet brouillon à votre nom. Aucun paiement, aucun engagement — uniquement lorsque vous décidez d'avancer.",
    name_ph: 'Votre nom *',
    email_ph: 'Email professionnel *',
    company_ph: 'Entreprise / nom de la marque',
    sector_ph: 'Secteur (optionnel)',
    submit: 'Lancer mon projet',
    confidentiality: 'Strictement confidentiel. Vos données ne sont jamais partagées en dehors de Grupo BMP.',
    error_required: 'Indiquez votre nom et un email valide.',
    success_label: 'Lead reçu',
    success_title: 'Merci, {name}.',
    success_body: 'Le projet {code} a été créé en brouillon. Notre équipe vous recontactera sous un jour ouvré.',
    success_cta: 'En attendant, découvrez les plans',
    sectors: [
      'Patrimoine et services financiers', 'Immobilier et développement', 'Hôtellerie et restauration',
      'Santé et bien-être', 'Commerce et e-commerce', 'Services professionnels',
      'Technologie et SaaS', 'Éducation et formation', 'Industrie et négoce', 'Autre',
    ],
  },
  ar: {
    eyebrow: 'ابدأ علامتك التجارية في 60 ثانية',
    title: 'أخبرنا عن مشروعك — ونحن نتكفل بالباقي.',
    note: 'ننشئ مشروعًا بمسودة باسمك. لا دفع، لا التزام — فقط عندما تقرر المضي قدمًا.',
    name_ph: 'اسمك *',
    email_ph: 'البريد المهني *',
    company_ph: 'الشركة / اسم العلامة',
    sector_ph: 'القطاع (اختياري)',
    submit: 'ابدأ مشروع علامتي',
    confidentiality: 'سرّي تمامًا. لن تتم مشاركة بياناتك خارج Grupo BMP أبدًا.',
    error_required: 'أدخل اسمك وبريدًا إلكترونيًا صحيحًا.',
    success_label: 'تم استلام طلبك',
    success_title: 'شكرًا، {name}.',
    success_body: 'أنشأنا المشروع {code} في حالة المسودة. سيتواصل معك فريقنا خلال يوم عمل واحد.',
    success_cta: 'في الأثناء، استكشف الخطط',
    sectors: [
      'الثروة والخدمات المالية', 'العقارات والتطوير', 'الضيافة والمأكولات',
      'الصحة والعافية', 'التجزئة والتجارة الإلكترونية', 'الخدمات المهنية',
      'التكنولوجيا و SaaS', 'التعليم والتدريب', 'الصناعة والتجارة', 'أخرى',
    ],
  },
  zh: {
    eyebrow: '60 秒启动你的品牌',
    title: '告诉我们你的项目——剩下的交给我们。',
    note: '我们以你的名义创建草稿项目。无需付款,无需承诺,只在你决定继续时才推进。',
    name_ph: '你的名字 *',
    email_ph: '工作邮箱 *',
    company_ph: '公司 / 品牌名称',
    sector_ph: '行业(可选)',
    submit: '启动我的品牌项目',
    confidentiality: '严格保密。您的数据绝不与 Grupo BMP 之外的任何方共享。',
    error_required: '请输入您的姓名和有效邮箱。',
    success_label: '已收到线索',
    success_title: '谢谢,{name}。',
    success_body: '我们已创建草稿项目 {code}。我们的团队将在一个工作日内与你联系。',
    success_cta: '同时,浏览方案',
    sectors: [
      '财富与金融服务', '房地产与开发', '酒店与餐饮',
      '健康与养生', '零售与电商', '专业服务',
      '科技与 SaaS', '教育与培训', '工业与贸易', '其他',
    ],
  },
};

const BrandStudioLeadForm = ({ source = 'public_form', variant = 'dark' }) => {
  const { language } = useLanguage();
  const t = COPY[language] || COPY.en;
  const [form, setForm] = useState({ name: '', email: '', company_name: '', sector: '' });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const isDark = variant === 'dark';

  const handleChange = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.name.trim() || !form.email.includes('@')) {
      setError(t.error_required);
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API_URL}/api/brand/leads`, { ...form, source });
      setResult(r.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || t.error_required);
    } finally {
      setSubmitting(false);
    }
  };

  if (result?.success) {
    const firstName = form.name.split(' ')[0];
    return (
      <div
        className={`rounded-2xl p-8 ${
          isDark ? 'bg-white/5 border border-white/15' : 'bg-white border border-gray-200'
        }`}
        data-testid="brand-lead-success"
      >
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-500/15 text-green-300 text-[11px] font-bold uppercase tracking-widest mb-3">
          <CheckCircle2 className="w-3.5 h-3.5" /> {t.success_label}
        </div>
        <h4 className={`text-2xl font-extrabold leading-tight mb-2 ${isDark ? 'text-white' : 'text-[#0F172A]'}`}>
          {t.success_title.replace('{name}', firstName)}
        </h4>
        <p className={`text-sm font-light mb-4 ${isDark ? 'text-white/70' : 'text-[#475569]'}`}>
          {t.success_body.split('{code}').map((chunk, i, arr) => (
            <React.Fragment key={i}>
              {chunk}
              {i < arr.length - 1 && <span className="font-mono font-bold">{result.project_code}</span>}
            </React.Fragment>
          ))}
        </p>
        <Link to="/brand-studio">
          <button
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold ${
              isDark
                ? 'bg-gradient-to-r from-[#FF6B35] to-amber-400 text-[#0F172A] hover:scale-[1.03] transition'
                : 'bg-[#0F172A] text-white hover:bg-[#1E293B]'
            }`}
          >
            {t.success_cta} <ArrowRight className="w-4 h-4" />
          </button>
        </Link>
      </div>
    );
  }

  const baseInput = `w-full px-4 py-3 rounded-xl text-sm outline-none transition ${
    isDark
      ? 'bg-white/10 text-white placeholder:text-white/40 border border-white/15 focus:border-[#FF6B35] focus:bg-white/15'
      : 'bg-white text-[#0F172A] placeholder:text-gray-400 border border-gray-300 focus:border-[#FF6B35]'
  }`;

  return (
    <form
      onSubmit={handleSubmit}
      className={`rounded-2xl p-6 md:p-8 ${
        isDark ? 'bg-white/5 backdrop-blur border border-white/15' : 'bg-white border border-gray-200 shadow-md'
      }`}
      data-testid="brand-lead-form"
    >
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className={`w-4 h-4 ${isDark ? 'text-amber-300' : 'text-[#FF6B35]'}`} />
        <span className={`text-[11px] font-bold uppercase tracking-[0.2em] ${isDark ? 'text-amber-300' : 'text-[#FF6B35]'}`}>
          {t.eyebrow}
        </span>
      </div>
      <h4 className={`text-xl md:text-2xl font-extrabold leading-tight mb-1 ${isDark ? 'text-white' : 'text-[#0F172A]'}`}>
        {t.title}
      </h4>
      <p className={`text-xs mb-5 ${isDark ? 'text-white/60' : 'text-[#94A3B8]'}`}>
        {t.note}
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <input
          type="text"
          value={form.name}
          onChange={handleChange('name')}
          placeholder={t.name_ph}
          className={baseInput}
          data-testid="lead-name-input"
          required
        />
        <input
          type="email"
          value={form.email}
          onChange={handleChange('email')}
          placeholder={t.email_ph}
          className={baseInput}
          data-testid="lead-email-input"
          required
        />
        <input
          type="text"
          value={form.company_name}
          onChange={handleChange('company_name')}
          placeholder={t.company_ph}
          className={baseInput}
          data-testid="lead-company-input"
        />
        <select
          value={form.sector}
          onChange={handleChange('sector')}
          className={baseInput}
          data-testid="lead-sector-input"
        >
          <option value="">{t.sector_ph}</option>
          {t.sectors.map((s) => (
            <option key={s} value={s} className="text-[#0F172A]">{s}</option>
          ))}
        </select>
      </div>

      {error && (
        <div className={`mt-3 text-xs ${isDark ? 'text-red-300' : 'text-red-600'}`} data-testid="lead-error">
          {error}
        </div>
      )}

      <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
        <p className={`text-[11px] ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
          {t.confidentiality}
        </p>
        <button
          type="submit"
          disabled={submitting}
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-[#FF6B35] to-amber-400 text-[#0F172A] text-sm font-extrabold tracking-wide shadow-lg hover:scale-[1.03] transition disabled:opacity-60 disabled:hover:scale-100"
          data-testid="lead-submit-btn"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          {t.submit}
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </form>
  );
};

export default BrandStudioLeadForm;
